/**
 * Phase 125 — Academy Registration at /arenax/host/academy (6-step wizard).
 *
 *   1. Identity      — name, sports, owner name, phone, email
 *   2. Location      — city, area, address, landmark, map pin
 *   3. Infrastructure — turf count, ground size, indoor/outdoor, floodlights,
 *                       washrooms, parking, seating
 *   4. Booking       — open slots, pricing, peak pricing, subscription
 *   5. Media         — logo, cover, turf gallery
 *   6. Publish       — confirm + publish-immediately toggle
 *
 * For federated `academy_owner`s the wizard surfaces the auto-link path
 * with pre-filled fields from `GET /api/academy`.  On success grants the
 * `manage_academy` capability + shows a city-native celebration overlay.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useArenaxCapabilities } from '@/lib/arenaxCapabilities';
import ArenaxLayout from './ArenaxLayout';
import { ArenaxWordmark } from './ArenaxWordmark';
import ArenaxLeafletPicker from './ArenaxLeafletPicker';
import { ArenaxImageUploader, ArenaxGalleryUploader } from './ArenaxImageUploader';
import ArenaxActivationCelebration from './ArenaxActivationCelebration';
import {
  Building2, ArrowRight, ArrowLeft, CheckCircle2, Loader2, Sparkles,
} from 'lucide-react';

const SPORTS = [
  'Football', 'Cricket', 'Hockey', 'Badminton', 'Basketball',
  'Tennis', 'Volleyball', 'Swimming', 'Athletics', 'Pickleball',
  'Table Tennis', 'Chess', 'Multi-Sport',
];

const STEPS = [
  { id: 'identity', label: 'Identity' },
  { id: 'location', label: 'Location' },
  { id: 'infrastructure', label: 'Infrastructure' },
  { id: 'booking', label: 'Booking' },
  { id: 'media', label: 'Media' },
  { id: 'publish', label: 'Publish' },
];

export default function ArenaxHostAcademy() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { refresh: refreshCapabilities } = useArenaxCapabilities();
  const [step, setStep] = useState(0);
  const [opStatus, setOpStatus] = useState(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [celebrating, setCelebrating] = useState(null);
  const [form, setForm] = useState({
    // Step 1 — Identity
    name: '',
    primary_sport: 'Football',
    sports: ['Football'],
    owner_name: '',
    phone: '',
    email: '',
    // Step 2 — Location
    city: '',
    area: '',
    address: '',
    landmark: '',
    geo: { lat: null, lng: null },
    // Step 3 — Infrastructure
    infrastructure: {
      turf_count: 1, ground_size_sqft: null, indoor: false, outdoor: true,
      floodlights: true, washrooms: true, parking: true, seating_capacity: null,
    },
    // Step 4 — Booking
    booking: {
      open_slots: true, base_price_per_hour: null,
      peak_price_per_hour: null, subscription_available: true,
    },
    // Step 5 — Media
    logo_image: null,
    cover_image: null,
    gallery_images: [],
    // Step 6 — Publish
    tagline: '',
    description: '',
    publish_immediately: true,
  });

  useEffect(() => {
    if (!user) { setBootLoading(false); return; }
    let stop = false;
    (async () => {
      try {
        const sRes = await api.get('/arenax/me/operator-status');
        if (stop) return;
        setOpStatus(sRes.data);
        // Pre-fill base identity from logged-in user
        setForm(prev => ({
          ...prev,
          owner_name: prev.owner_name || user.name || '',
          email: prev.email || user.email || '',
        }));
        if (sRes.data?.has_academy && user?.academy_id) {
          try {
            const aRes = await api.get('/academy');
            const a = aRes.data || {};
            setForm(prev => ({
              ...prev,
              name: a.name || prev.name,
              city: a.city || prev.city,
              address: a.address || prev.address,
              phone: a.phone || prev.phone,
              sports: a.sport_types?.length ? a.sport_types : prev.sports,
              primary_sport: a.sport_types?.[0] || prev.primary_sport,
            }));
          } catch (_e) { /* non-blocking */ }
        }
      } catch (_e) { /* non-blocking */ }
      finally { if (!stop) setBootLoading(false); }
    })();
    return () => { stop = true; };
  }, [user]);

  const isFederatedOwner = !!(opStatus?.has_academy && user?.academy_id);
  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const updateNested = (parent, k, v) => setForm(prev => ({
    ...prev, [parent]: { ...prev[parent], [k]: v },
  }));
  const toggleSport = (sport) => setForm(prev => {
    const has = prev.sports.includes(sport);
    const next = has ? prev.sports.filter(s => s !== sport) : [...prev.sports, sport];
    return {
      ...prev,
      sports: next,
      primary_sport: next.length === 0 ? '' : next.includes(prev.primary_sport) ? prev.primary_sport : next[0],
    };
  });

  const canAdvance = useMemo(() => {
    if (step === 0) return (form.name || '').trim().length > 1 && form.sports.length > 0;
    if (step === 1) return (form.city || '').trim().length > 0;
    return true;
  }, [step, form]);

  const submit = async () => {
    if (!user) { navigate('/arenax/auth/login'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        logo_image: form.logo_image?.path || form.logo_image?.url || null,
        cover_image: form.cover_image?.path || form.cover_image?.url || null,
        gallery_images: form.gallery_images.map((g, i) => ({
          url: g.url || g.path,
          display_order: g.display_order ?? i,
          caption: g.caption || null,
        })),
      };
      await api.post('/arenax/host/academy', payload);
      setCelebrating({ city: form.city });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not register academy');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <ArenaxLayout>
        <section className="max-w-md mx-auto px-5 py-20 text-center arenax-themed">
          <Building2 className="h-10 w-10 mx-auto mb-3" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
          <h1 className="text-2xl font-bold mb-2"
              style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, serif' }}>
            Sign in to register your academy
          </h1>
          <p className="text-sm mb-6" style={{ color: 'hsl(var(--ax-text-muted))' }}>
            One ArenaX identity unlocks Academy, Venue and Organizer capabilities.
          </p>
          <button
            data-testid="host-academy-signin-cta"
            onClick={() => navigate('/arenax/auth/login')}
            className="px-5 py-2.5 rounded-xl text-sm font-bold"
            style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}
          >
            Sign in to ArenaX
          </button>
        </section>
      </ArenaxLayout>
    );
  }

  return (
    <ArenaxLayout>
      {celebrating && (
        <ArenaxActivationCelebration
          kind="academy"
          city={celebrating.city}
          continueLabel="Open your academy hub"
          onContinue={() => navigate('/arenax/host?activated=academy')}
        />
      )}

      <section className="max-w-3xl mx-auto px-5 py-10 arenax-themed" data-testid="arenax-host-academy-page">
        <div className="text-center mb-8 space-y-2">
          <div className="inline-flex items-center justify-center mb-1">
            <ArenaxWordmark size="md" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight"
              style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, "Times New Roman", serif' }}>
            {isFederatedOwner ? 'Bring your academy into the city network.' : 'Plant your academy on ArenaX.'}
          </h1>
          <p className="text-sm max-w-md mx-auto" style={{ color: 'hsl(var(--ax-text-muted))' }}>
            {isFederatedOwner
              ? 'We\'ve pre-filled your Out-Play academy. Confirm, enrich, publish.'
              : 'A few steps and your academy is part of the city\'s sports network.'}
          </p>
        </div>

        {/* Step progress */}
        <div className="flex items-center justify-center gap-1 mb-8 overflow-x-auto pb-1" data-testid="host-academy-stepper">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => i <= step && setStep(i)}
                className="h-7 px-3 rounded-full inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wider transition-all flex-shrink-0"
                style={{
                  background: i === step
                    ? 'var(--ax-primary-gradient)'
                    : i < step ? 'rgba(16,185,129,0.10)' : 'var(--ax-card-bg-soft)',
                  color: i === step
                    ? 'hsl(var(--ax-button-text))'
                    : i < step ? '#047857' : 'hsl(var(--ax-text-muted))',
                  border: i === step ? 'none' : '1px solid var(--ax-border-quiet)',
                  cursor: i <= step ? 'pointer' : 'default',
                }}
                data-testid={`host-academy-step-${i}`}
              >
                {i < step ? <CheckCircle2 className="h-3 w-3" /> : <span>{i + 1}</span>}
                <span className="uppercase">{s.label}</span>
              </button>
              {i < STEPS.length - 1 && <span style={{ color: 'hsl(var(--ax-text-faint))' }}>·</span>}
            </div>
          ))}
        </div>

        {bootLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
          </div>
        ) : (
          <div className="arenax-card arenax-operational-form p-6 sm:p-8" style={{ borderRadius: 16 }}>

            {/* ---- STEP 1 — Identity ---- */}
            {step === 0 && (
              <div className="space-y-5" data-testid="host-academy-step-identity">
                {isFederatedOwner && (
                  <div className="flex items-start gap-2 p-3 rounded-lg text-xs"
                       style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.20)', color: '#065f46' }}>
                    <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      We detected your Out-Play academy. These fields are pre-filled — confirm or refine.
                    </span>
                  </div>
                )}
                <Field label="Academy name">
                  <input
                    data-testid="host-academy-name"
                    value={form.name}
                    onChange={e => update('name', e.target.value)}
                    placeholder="e.g. Vijayawada Blues FC"
                    className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                  />
                </Field>
                <Field label="Primary sport">
                  <select
                    data-testid="host-academy-primary-sport"
                    value={form.primary_sport}
                    onChange={e => {
                      const v = e.target.value;
                      update('primary_sport', v);
                      if (!form.sports.includes(v)) update('sports', [...form.sports, v]);
                    }}
                    className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                  >
                    {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Sports supported">
                  <div className="flex flex-wrap gap-2" data-testid="host-academy-sports">
                    {SPORTS.map(s => {
                      const active = form.sports.includes(s);
                      return (
                        <button
                          key={s}
                          type="button"
                          data-testid={`host-academy-sport-${s.toLowerCase().replace(/\s/g, '-')}`}
                          onClick={() => toggleSport(s)}
                          className={`arenax-chip ${active ? 'is-active' : ''} text-xs px-3 py-1.5 rounded-full transition-all`}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Owner name">
                    <input
                      data-testid="host-academy-owner-name"
                      value={form.owner_name}
                      onChange={e => update('owner_name', e.target.value)}
                      placeholder="Your full name"
                      className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                    />
                  </Field>
                  <Field label="Phone">
                    <input
                      data-testid="host-academy-phone"
                      value={form.phone}
                      onChange={e => update('phone', e.target.value)}
                      placeholder="+91 98765 43210"
                      className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                    />
                  </Field>
                </div>
                <Field label="Contact email">
                  <input
                    type="email"
                    data-testid="host-academy-email"
                    value={form.email}
                    onChange={e => update('email', e.target.value)}
                    placeholder="you@academy.in"
                    className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                  />
                </Field>
              </div>
            )}

            {/* ---- STEP 2 — Location ---- */}
            {step === 1 && (
              <div className="space-y-5" data-testid="host-academy-step-location">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div className="space-y-4">
                    <Field label="City">
                      <input
                        data-testid="host-academy-city"
                        value={form.city}
                        onChange={e => update('city', e.target.value)}
                        placeholder="Bengaluru"
                        className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                      />
                    </Field>
                    <Field label="Area / Neighborhood">
                      <input
                        data-testid="host-academy-area"
                        value={form.area}
                        onChange={e => update('area', e.target.value)}
                        placeholder="HSR Layout"
                        className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                      />
                    </Field>
                    <Field label="Full address">
                      <input
                        data-testid="host-academy-address"
                        value={form.address}
                        onChange={e => update('address', e.target.value)}
                        placeholder="14th Cross, Sector 7, HSR Layout"
                        className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                      />
                    </Field>
                    <Field label="Landmark">
                      <input
                        data-testid="host-academy-landmark"
                        value={form.landmark}
                        onChange={e => update('landmark', e.target.value)}
                        placeholder="Opposite Phoenix Mall"
                        className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <div className="text-[11px] tracking-wider uppercase font-bold"
                         style={{ color: 'hsl(var(--ax-text-muted))' }}>
                      Drop a pin
                    </div>
                    <ArenaxLeafletPicker value={form.geo} onChange={geo => update('geo', geo)} />
                    {form.geo?.lat ? (
                      <p className="text-[11px]" style={{ color: 'hsl(var(--ax-text-muted))' }}>
                        Pin: {form.geo.lat.toFixed(5)}, {form.geo.lng.toFixed(5)}
                      </p>
                    ) : (
                      <p className="text-[11px] italic" style={{ color: 'hsl(var(--ax-text-faint))' }}>
                        Click anywhere on the map to set your academy's location.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ---- STEP 3 — Infrastructure ---- */}
            {step === 2 && (
              <div className="space-y-5" data-testid="host-academy-step-infrastructure">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Field label="Turf / Court count">
                    <input
                      type="number" min={1}
                      data-testid="host-academy-turf-count"
                      value={form.infrastructure.turf_count ?? ''}
                      onChange={e => updateNested('infrastructure', 'turf_count', e.target.value ? Number(e.target.value) : null)}
                      className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                    />
                  </Field>
                  <Field label="Ground size (sq ft)">
                    <input
                      type="number" min={0}
                      data-testid="host-academy-ground-size"
                      value={form.infrastructure.ground_size_sqft ?? ''}
                      onChange={e => updateNested('infrastructure', 'ground_size_sqft', e.target.value ? Number(e.target.value) : null)}
                      placeholder="20000"
                      className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                    />
                  </Field>
                  <Field label="Seating capacity">
                    <input
                      type="number" min={0}
                      data-testid="host-academy-seating"
                      value={form.infrastructure.seating_capacity ?? ''}
                      onChange={e => updateNested('infrastructure', 'seating_capacity', e.target.value ? Number(e.target.value) : null)}
                      placeholder="50"
                      className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { k: 'indoor', label: 'Indoor' },
                    { k: 'outdoor', label: 'Outdoor' },
                    { k: 'floodlights', label: 'Floodlights' },
                    { k: 'washrooms', label: 'Washrooms' },
                    { k: 'parking', label: 'Parking' },
                  ].map(opt => (
                    <label
                      key={opt.k}
                      className="flex items-center gap-2 cursor-pointer px-3 py-2.5 rounded-lg border"
                      style={{ borderColor: 'var(--ax-border-quiet)', background: 'var(--ax-card-bg-soft)' }}
                      data-testid={`host-academy-infra-${opt.k}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!form.infrastructure[opt.k]}
                        onChange={e => updateNested('infrastructure', opt.k, e.target.checked)}
                        className="h-3.5 w-3.5"
                      />
                      <span className="text-xs font-semibold" style={{ color: 'hsl(var(--ax-text))' }}>
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* ---- STEP 4 — Booking ---- */}
            {step === 3 && (
              <div className="space-y-5" data-testid="host-academy-step-booking">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Base price / hour (₹)">
                    <input
                      type="number" min={0}
                      data-testid="host-academy-base-price"
                      value={form.booking.base_price_per_hour ?? ''}
                      onChange={e => updateNested('booking', 'base_price_per_hour', e.target.value ? Number(e.target.value) : null)}
                      placeholder="1500"
                      className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                    />
                  </Field>
                  <Field label="Peak price / hour (₹)" helper="Floodlit evenings, weekends.">
                    <input
                      type="number" min={0}
                      data-testid="host-academy-peak-price"
                      value={form.booking.peak_price_per_hour ?? ''}
                      onChange={e => updateNested('booking', 'peak_price_per_hour', e.target.value ? Number(e.target.value) : null)}
                      placeholder="2200"
                      className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="flex items-start gap-2 px-3 py-2.5 rounded-lg border cursor-pointer"
                         style={{ borderColor: 'var(--ax-border-quiet)', background: 'var(--ax-card-bg-soft)' }}
                         data-testid="host-academy-open-slots">
                    <input type="checkbox"
                           checked={form.booking.open_slots}
                           onChange={e => updateNested('booking', 'open_slots', e.target.checked)}
                           className="mt-0.5 h-3.5 w-3.5" />
                    <span className="text-xs" style={{ color: 'hsl(var(--ax-text))' }}>
                      <span className="font-bold block">Open slots immediately</span>
                      <span style={{ color: 'hsl(var(--ax-text-muted))' }}>
                        Players can book the moment you go live.
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 px-3 py-2.5 rounded-lg border cursor-pointer"
                         style={{ borderColor: 'var(--ax-border-quiet)', background: 'var(--ax-card-bg-soft)' }}
                         data-testid="host-academy-subscription">
                    <input type="checkbox"
                           checked={form.booking.subscription_available}
                           onChange={e => updateNested('booking', 'subscription_available', e.target.checked)}
                           className="mt-0.5 h-3.5 w-3.5" />
                    <span className="text-xs" style={{ color: 'hsl(var(--ax-text))' }}>
                      <span className="font-bold block">Subscription / packages</span>
                      <span style={{ color: 'hsl(var(--ax-text-muted))' }}>
                        Weekly slots, monthly memberships.
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* ---- STEP 5 — Media ---- */}
            {step === 4 && (
              <div className="space-y-5" data-testid="host-academy-step-media">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ArenaxImageUploader
                    label="Logo"
                    kind="logo"
                    value={form.logo_image}
                    onChange={img => update('logo_image', img)}
                    testId="host-academy-logo-upload"
                    helper="Your academy brand mark."
                  />
                  <ArenaxImageUploader
                    label="Cover image"
                    kind="cover"
                    value={form.cover_image}
                    onChange={img => update('cover_image', img)}
                    testId="host-academy-cover-upload"
                    helper="Hero shot — turf, team, signature moment."
                  />
                </div>
                <ArenaxGalleryUploader
                  label="Turf gallery"
                  value={form.gallery_images}
                  onChange={g => update('gallery_images', g)}
                  testId="host-academy-gallery"
                  helper="4-8 sharp shots — turf, floodlights, training, trophies."
                />
              </div>
            )}

            {/* ---- STEP 6 — Publish ---- */}
            {step === 5 && (
              <div className="space-y-5" data-testid="host-academy-step-publish">
                <Field label="Tagline" helper="One line. Show up in the city feed.">
                  <input
                    data-testid="host-academy-tagline"
                    value={form.tagline}
                    onChange={e => update('tagline', e.target.value)}
                    placeholder="The grassroots academy that built the city."
                    className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                  />
                </Field>
                <Field label="Description">
                  <textarea
                    data-testid="host-academy-description"
                    value={form.description}
                    onChange={e => update('description', e.target.value)}
                    placeholder="Founded in 2018, we run age-grouped programs from U-7 to U-19…"
                    rows={4}
                    className="arenax-input w-full px-4 py-3 rounded-xl text-sm resize-none"
                  />
                </Field>
                <div className="p-4 rounded-xl text-sm"
                     style={{ background: 'var(--ax-card-bg-soft)', border: '1px solid var(--ax-border-quiet)' }}>
                  <div className="text-[11px] uppercase tracking-wider mb-1"
                       style={{ color: 'hsl(var(--ax-text-faint))' }}>Ready to review</div>
                  <div className="font-bold text-base mb-2" style={{ color: 'hsl(var(--ax-text))' }}>
                    {form.name || '—'} · {form.city || '—'}{form.area ? ` · ${form.area}` : ''}
                  </div>
                  <div className="text-xs" style={{ color: 'hsl(var(--ax-text-muted))' }}>
                    Sports: {form.sports.join(', ') || '—'} · Turfs: {form.infrastructure.turf_count || '—'}
                    {form.booking.base_price_per_hour ? ` · ₹${form.booking.base_price_per_hour}/hr` : ''}
                  </div>
                </div>
                <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border transition-all"
                       style={{ borderColor: form.publish_immediately ? 'rgba(16,185,129,0.40)' : 'var(--ax-border-quiet)',
                                background: form.publish_immediately ? 'rgba(16,185,129,0.06)' : 'transparent' }}
                       data-testid="host-academy-publish-toggle">
                  <input
                    type="checkbox"
                    checked={form.publish_immediately}
                    onChange={e => update('publish_immediately', e.target.checked)}
                    className="mt-1 h-4 w-4 cursor-pointer"
                  />
                  <div>
                    <div className="text-sm font-bold" style={{ color: 'hsl(var(--ax-text))' }}>
                      Publish to the city pulse immediately
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: 'hsl(var(--ax-text-muted))' }}>
                      Your academy shows up in venue searches and player discovery. Untick to save as a draft.
                    </div>
                  </div>
                </label>
              </div>
            )}

            {/* Footer nav */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t"
                 style={{ borderColor: 'var(--ax-border-quiet)' }}>
              <button
                type="button"
                onClick={() => step > 0 ? setStep(step - 1) : navigate('/arenax/host')}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg"
                style={{ color: 'hsl(var(--ax-text-muted))' }}
                data-testid="host-academy-back"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {step === 0 ? 'Back to hub' : 'Back'}
              </button>
              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={() => canAdvance && setStep(step + 1)}
                  disabled={!canAdvance}
                  className="inline-flex items-center gap-1.5 text-xs font-bold py-2.5 px-5 rounded-lg transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                  style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}
                  data-testid="host-academy-next"
                >
                  Continue <ArrowRight className="h-3.5 w-3.5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 text-xs font-bold py-2.5 px-5 rounded-lg transition-all hover:-translate-y-0.5 disabled:opacity-60"
                  style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))',
                           boxShadow: 'var(--ax-shadow-glow)' }}
                  data-testid="host-academy-submit"
                >
                  {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  {isFederatedOwner ? 'Link to ArenaX' : 'Publish academy'}
                </button>
              )}
            </div>
          </div>
        )}
      </section>
    </ArenaxLayout>
  );
}

function Field({ label, helper, children }) {
  return (
    <div>
      <label className="text-[11px] tracking-wider uppercase font-bold block mb-1.5"
             style={{ color: 'hsl(var(--ax-text-muted))' }}>
        {label}
      </label>
      {children}
      {helper && (
        <p className="text-[11px] mt-1.5" style={{ color: 'hsl(var(--ax-text-faint))' }}>{helper}</p>
      )}
    </div>
  );
}
